import { forwardRef } from 'react';
import { cn } from '@/hen-ui/utils';
import { Button } from '@/hen-ui/atomics/Button';
import { CardRoot } from '@/hen-ui/composites/Card/Card';
import { Flex } from '@/hen-ui/composites/Flex';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from '@/hen-ui/composites/Drawer';
import { ExpandCollapseFooter } from './ExpandCollapseFooter';
import type { CollapsibleCardProps } from './types';

export const CollapsibleCard = forwardRef<HTMLDivElement, CollapsibleCardProps>(
  (
    {
      expanded = false,
      onExpandedChange,
      modalOpen = false,
      onModalOpenChange,
      ariaLabel,
      renderHeader,
      renderBody,
      footerLeft,
      footerClassName,
      showToggle = true,
      renderDrawerHeader,
      renderDrawerBody,
      drawerFooterLeft,
      drawerHeaderClassName,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <CardRoot
        ref={ref}
        className={className}
        role='region'
        aria-label={ariaLabel}
        {...props}
      >
        {renderHeader()}

        {renderBody(expanded)}

        {(showToggle || footerLeft) && (
          <Flex
            asChild
            direction='row'
            align='center'
            justify={footerLeft ? 'between' : 'end'}
            className={footerClassName}
          >
            <footer>
              {footerLeft}
              {showToggle && (
                <ExpandCollapseFooter
                  expanded={expanded}
                  modalOpen={modalOpen}
                  onExpandedChange={onExpandedChange}
                  onModalOpenChange={onModalOpenChange}
                />
              )}
            </footer>
          </Flex>
        )}

        <Drawer open={modalOpen} onOpenChange={onModalOpenChange}>
          <DrawerContent className='[&>div:first-child]:mt-0 [&>div:first-child]:h-0'>
            <div className='flex-1 overflow-y-auto'>
              <DrawerHeader className={cn('text-left', drawerHeaderClassName)}>
                {renderDrawerHeader()}
              </DrawerHeader>
              <div className='px-lg py-lg'>{renderDrawerBody()}</div>
            </div>

            <DrawerFooter
              className={cn(
                'px-lg py-md border-t border-neutral-200 flex-row items-center mt-0 gap-0',
                drawerFooterLeft ? 'justify-between' : 'justify-end',
              )}
            >
              {drawerFooterLeft}
              <Button
                variant='textOnly'
                size='flush'
                iconBefore='chevron-up'
                className='text-primary-700 font-bold text-l1'
                onClick={() => onModalOpenChange?.(false)}
              >
                Collapse Details
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </CardRoot>
    );
  },
);

CollapsibleCard.displayName = 'CollapsibleCard';
